import type { ServiceHealth, ServiceSummary } from '@apm/contracts';
import { formatCount, formatRelative } from '../time';

const HEALTH_ORDER: Record<ServiceHealth, number> = {
  critical: 0,
  warning: 1,
  ok: 2,
};

/** 위험한 서비스부터, 같은 상태면 이름순으로 둔다. */
export function sortServices(rows: readonly ServiceSummary[]): ServiceSummary[] {
  return [...rows].sort(
    (a, b) => HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health] || a.service.localeCompare(b.service),
  );
}

/**
 * 인스턴스 줄에 적을 말 (34회차 리뷰 반영).
 *
 * **끊김과 조용함은 다른 일이다.** 실시간 인스턴스(1초 보고)가 조용해진 것은 신호가 끊긴
 * 것이고, 트레이스만 인스턴스가 조용한 것은 그동안 요청이 없었다는 뜻이다. 한 서비스에 둘이
 * 섞일 수 있어 서비스 단위 `realtime`만으로는 가를 수 없다. 서버가 나눠 준 수를 쓰고,
 * 그 값이 없는 옛 응답에서는 서비스 단위 표시로 되돌아간다.
 */
export function instanceLine(row: ServiceSummary): { label: string; warn: boolean } {
  const total = `${formatCount(row.instances.total)}개`;
  if (row.instances.stale === 0) return { label: total, warn: false };
  const cut = row.instances.staleRealtime;
  const quiet = row.instances.staleTraceOnly;
  // 옛 서버 응답에는 나눈 수가 없다. 서비스가 트레이스만이면 통째로 조용함이다
  if (cut === undefined || quiet === undefined) {
    const word = row.realtime ? '끊김' : '조용함';
    return {
      label: `${total} · ${word} ${formatCount(row.instances.stale)}개`,
      warn: row.realtime,
    };
  }
  const parts: string[] = [];
  if (cut > 0) parts.push(`끊김 ${formatCount(cut)}개`);
  if (quiet > 0) parts.push(`조용함 ${formatCount(quiet)}개`);
  return { label: [total, ...parts].join(' · '), warn: cut > 0 };
}

/**
 * 카드 오른쪽 위의 마지막 수신 문구 (34회차 리뷰 반영).
 *
 * 관제 서버가 다시 뜨면 생존 판정의 기억이 비어 "받은 적 없음"으로 보인다. 어제까지 잘 돌던
 * 앱이 처음 보는 앱처럼 보이는 자리라, 저장에 남은 마지막 기록으로 답할 때는 그렇다고 적는다.
 */
export function lastSeenLine(row: ServiceSummary, now?: number): string {
  if (row.lastSeenAt === undefined) return '받은 적 없음';
  const when = formatRelative(row.lastSeenAt, now);
  return row.lastSeenFromStorage ? `저장된 마지막 기록 ${when}` : `마지막 수신 ${when}`;
}

/**
 * 화면의 모든 서비스 고르는 칸이 함께 쓰는 목록.
 * `only`를 주면 그 이름들이 선택지다 (52회차). 일반 계정의 허용 서비스로, 자료가 아직 없는 것도 낸다
 */
export function serviceOptions(
  rows: readonly ServiceSummary[],
  selected = '',
  only?: readonly string[],
) {
  const names =
    only !== undefined
      ? [...only]
      : rows.filter((row) => !row.nameFromFolder).map((row) => row.service);
  if (selected !== '' && !names.includes(selected)) names.push(selected);
  names.sort((a, b) => a.localeCompare(b));
  if (names.length === 0) return [{ value: '', label: '등록된 서비스 없음' }];
  const options = names.map((name) => ({ value: name, label: name }));
  // 대시보드의 전체 보기에서는 값이 비어 있다. 첫 서비스를 골랐다고 잘못 보이지 않게 한다.
  return selected === '' ? [{ value: '', label: '서비스를 고를 것' }, ...options] : options;
}
